import { Option, TimeFilterValue, TimePeriod } from '@hakimo-ui/hakimo/types';
import { SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import dayjs from 'dayjs';
import dayjsTimezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useMemo } from 'react';
import { allTimePeriodOptions } from '../constants';

dayjs.extend(utc);
dayjs.extend(dayjsTimezone);

interface Props {
  value: TimeFilterValue;
  onChange: (value: TimeFilterValue) => void;
}

function TimeFilter(props: Props) {
  const { value, onChange } = props;

  // Memoize expensive calculations to prevent unnecessary re-renders
  const periodOptions = useMemo(
    () =>
      allTimePeriodOptions.filter((opt) => opt.name !== TimePeriod.CUSTOM_TIME),
    []
  );

  const selected = useMemo(
    () => periodOptions.find((item) => item.name === value.period),
    [periodOptions, value.period]
  );

  // Memoize formatted date values to prevent unnecessary dayjs conversions
  const startDateValue = useMemo(
    () =>
      value.customRangeStart
        ? dayjs(value.customRangeStart).format('YYYY-MM-DDTHH:mm')
        : '',
    [value.customRangeStart]
  );

  const endDateValue = useMemo(
    () =>
      value.customRangeEnd
        ? dayjs(value.customRangeEnd).format('YYYY-MM-DDTHH:mm')
        : '',
    [value.customRangeEnd]
  );

  const onChangeTimePeriod = (option: Option<TimePeriod>) => {
    onChange({
      period: option.name,
      customRangeStart: 0,
      customRangeEnd: 0,
    });
  };

  const onChangeCustomRangeStart: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    // Update end date also if it is not set to make it easier for the user
    const startTimestamp = new Date(event.target.value).getTime();
    let newEnd = value.customRangeEnd;
    if (value.customRangeEnd === 0) {
      newEnd = startTimestamp;
    }
    onChange({
      ...value,
      customRangeStart: startTimestamp,
      customRangeEnd: newEnd,
    });
  };

  const onChangeCustomRangeEnd: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    onChange({
      ...value,
      customRangeEnd: new Date(event.target.value).getTime(),
    });
  };

  return (
    <div>
      <SelectMenu
        label="Period"
        items={periodOptions}
        value={selected}
        onChange={onChangeTimePeriod}
        displayValue={(item) => (item ? item.name : '--Select--')}
      />
      {selected?.name === TimePeriod.CUSTOM_RANGE && (
        <div className="space-y-1">
          <label className="block">
            <span className="dark:text-dark-text text-xs text-gray-700">
              Start Date
            </span>
            <input
              type="datetime-local"
              className="dark:bg-dark-bg mt-1 block w-full rounded border-gray-400 text-sm dark:border-gray-600 dark:text-white dark:[color-scheme:dark]"
              value={startDateValue}
              onChange={onChangeCustomRangeStart}
            />
          </label>
          <label className="block">
            <span className="dark:text-dark-text text-xs text-gray-700">
              End Date
            </span>
            <input
              type="datetime-local"
              className="dark:bg-dark-bg mt-1 block w-full rounded border-gray-400 text-sm dark:border-gray-600 dark:text-white dark:[color-scheme:dark]"
              value={endDateValue}
              onChange={onChangeCustomRangeEnd}
            />
          </label>
        </div>
      )}
    </div>
  );
}

export default TimeFilter;
